import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

export interface SelectOption {
    label: string;
    value: string;
}

interface SelectProps {
    label?: string;
    placeholder?: string;
    options: SelectOption[];
    value?: string;
    onChange: (value: string) => void;
    error?: string;
    required?: boolean;
    searchable?: boolean;
    disabled?: boolean;
}

export default function Select({
    label,
    placeholder = "Select",
    options,
    value,
    onChange,
    error,
    required,
    searchable = true,
    disabled,
}: SelectProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [highlighted, setHighlighted] = useState(0);

    const filtered = options.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const selected = options.find((item) => item.value === value);

    useEffect(() => {
        const handler = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
                setSearch("");
            }
        };

        window.addEventListener("mousedown", handler);
        return () => window.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        if (open && searchable) {
            setTimeout(() => searchRef.current?.focus(), 50);
        }
        if (open) {
            const index = filtered.findIndex((item) => item.value === value);
            setHighlighted(index >= 0 ? index : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, searchable]);

    // Reset highlight when the filter changes so it never points past the list.
    useEffect(() => setHighlighted(0), [search]);

    // Keep the highlighted option in view while arrowing through.
    useEffect(() => {
        if (!open) return;
        const node = listRef.current?.children[highlighted] as HTMLElement | undefined;
        node?.scrollIntoView({ block: "nearest" });
    }, [highlighted, open]);

    const handleSelect = (option: SelectOption) => {
        onChange(option.value);
        setOpen(false);
        setSearch("");
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!open) {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlighted((prev) => Math.min(prev + 1, filtered.length - 1));
                break;

            case "ArrowUp":
                e.preventDefault();
                setHighlighted((prev) => Math.max(prev - 1, 0));
                break;

            case "Enter":
                e.preventDefault();
                if (filtered[highlighted]) handleSelect(filtered[highlighted]);
                break;

            case "Escape":
                setOpen(false);
                setSearch("");
                break;
        }
    };

    return (
        <div className="w-full" ref={wrapperRef}>
            {label && (
                <label className="mb-1 block text-sm text-white">
                    {label}
                    {required && <span className="ml-1 text-red-500">*</span>}
                </label>
            )}

            <div onKeyDown={handleKeyDown} className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen((prev) => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    className={`flex h-12 w-full items-center justify-between gap-2 rounded-xl border bg-white/10 px-4 text-left text-white backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400
                        ${error ? "border-red-500" : "border-white/20"}
                        ${disabled ? "cursor-not-allowed opacity-60" : "hover:bg-white/15"}`}
                >
                    <span className={selected ? "text-white" : "text-blue-200"}>
                        {selected?.label || placeholder}
                    </span>

                    <ChevronDown
                        className={`h-5 w-5 shrink-0 text-blue-200 transition ${open ? "rotate-180" : ""}`}
                    />
                </button>

                {open && (
                    <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-white/15 bg-[#101a33]/95 shadow-2xl backdrop-blur-md">
                        {searchable && (
                            <div className="border-b border-white/10 p-3">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-blue-200" />

                                    <input
                                        ref={searchRef}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search..."
                                        className="h-10 w-full rounded-lg border border-white/20 bg-white/10 pl-9 pr-3 text-white placeholder-blue-200 outline-none focus:ring-2 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        )}

                        <ul ref={listRef} role="listbox" className="max-h-60 overflow-y-auto py-1">
                            {filtered.length === 0 && (
                                <li className="p-4 text-center text-blue-200">No data found</li>
                            )}

                            {filtered.map((item, index) => (
                                <li
                                    key={item.value}
                                    role="option"
                                    aria-selected={item.value === value}
                                    onMouseEnter={() => setHighlighted(index)}
                                    onClick={() => handleSelect(item)}
                                    className={`flex cursor-pointer items-center justify-between px-4 py-3 text-white transition
                                        ${highlighted === index ? "bg-white/15" : ""}`}
                                >
                                    <span>{item.label}</span>

                                    {item.value === value && <Check className="h-4 w-4 text-blue-300" />}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
    );
}
